import { useEffect, useRef, useState, useMemo } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import axios from "axios";

export function AddMap(containerRef) {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // Initialize MapLibre map
    mapRef.current = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          base: {
            type: "raster",
            tiles: ["https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"],
            tileSize: 256,
          },
        },
        layers: [
          {
            id: "base",
            type: "raster",
            source: "base",
          },
        ],
      },
      center: [0, 0], // default center (will adjust later)
      zoom: 2,
      attributionControl: false,
      interactive: false,
    });

    // add attribution control
    mapRef.current.addControl(
      new maplibregl.AttributionControl({ compact: true }),
      "bottom-right"
    );

    // cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [containerRef]);

  return mapRef;
}


export function AddGeoJSON(mapRef, url, tableData = [], colorPalette = [], sourceId = "polygons", layerId = "polygons-layer") {
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const fetchAndColor = async () => {
      try {
        const response = await axios.get(url);
        const data = response.data;

        // Apply colors to features
        const coloredFeatures = (data.features || []).map(feature => {
          const featureName = feature?.properties?.name;
          const matchingItem = tableData.find(item => item.name === featureName);

          return {
            ...feature,
            properties: {
              ...feature?.properties,
              fillColor: matchingItem
                ? colorPalette[tableData.indexOf(matchingItem) % colorPalette.length]
                : "#ddd"
            }
          };
        });

        const coloredGeoData = { ...data, features: coloredFeatures };
        setGeoData(coloredGeoData);

        // Add or update source
        if (!map.getSource(sourceId)) {
          map.addSource(sourceId, { type: "geojson", data: coloredGeoData });
        } else {
          map.getSource(sourceId).setData(coloredGeoData);
        }

        // Add layer if it doesn't exist
        if (!map.getLayer(layerId)) {
          map.addLayer({
            id: layerId,
            type: "fill",
            source: sourceId,
            paint: {
              "fill-color": ["get", "fillColor"],
              "fill-opacity": 0.5,
              "fill-outline-color": "#333"
            }
          });
        }
      } catch (err) {
        console.error("Error loading GeoJSON:", err);
      }
    };

    fetchAndColor();

    // Cleanup
    return () => {
      if (!map.getStyle()) return;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [mapRef, url, tableData, colorPalette, sourceId, layerId]);

  return geoData;
}

export function AddLightpoints(mapRef, lightpoints = [], sourceId = "lightpoints", layerId = "lightpoints-layer") {
  const [pointData, setPointData] = useState(null);

  useEffect(() => {
    if (!mapRef.current || !lightpoints.length) return;
    const map = mapRef.current;

    const addSourceAndLayer = () => {
      const geojson = {
        type: "FeatureCollection",
        features: lightpoints,
      };
      setPointData(geojson);

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: "geojson", data: geojson });
      } else {
        map.getSource(sourceId).setData(geojson);
      }

      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: "circle",
          source: sourceId,
          paint: {
            "circle-radius": 4,
            "circle-color": "#f76e24",
          },
        });
      }
      
      map.on("click", layerId, (e) => {
        const feature = e.features?.[0];
        if (!feature) return;

        const coordinates = feature.geometry.coordinates.slice();
        const address =
          feature.properties?.locationstreet||
          "Adress unknown";

        new maplibregl.Popup()
          .setLngLat(coordinates)
          .setHTML(`<strong>${address}</strong>`)
          .addTo(map);
      });

      // 🔹 Change cursor on hover
      map.on("mouseenter", layerId, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
      });
    };

    if (!map.isStyleLoaded()) {
      map.once("load", addSourceAndLayer);
    } else {
      addSourceAndLayer();
    }

    // Cleanup
    return () => {
      if (!map.getStyle()) return;
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    };
  }, [mapRef, lightpoints, sourceId, layerId]);

  return pointData;
}

export  function FitBoundsPolygon(mapRef, geoData) {
  useEffect(() => {
    if (!mapRef.current || !geoData) return;
    const map = mapRef.current;

    const coordinates = geoData.features.flatMap((f) => {
      if (!f.geometry) return [];
      if (f.geometry.type === "Polygon") return f.geometry.coordinates.flat();
      if (f.geometry.type === "MultiPolygon") return f.geometry.coordinates.flat(2);
      return [];
    });

    if (!coordinates.length) return;

    const lats = coordinates.map((c) => c[1]);
    const lngs = coordinates.map((c) => c[0]);

    const bounds = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    map.fitBounds(bounds, { padding: 20, animate: true });
  }, [mapRef, geoData]);
}

export function FitBoundsPoints(mapRef, points = [], padding = 50) {
  useEffect(() => {
    if (!mapRef.current || !points.length) return;
    const map = mapRef.current;

    const coords = points
      .map((f) => f.geometry?.coordinates)
      .filter((c) => c && c.length === 2);

    if (!coords.length) return;

    const lats = coords.map((c) => c[1]);
    const lngs = coords.map((c) => c[0]);

    const bounds = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    if (!map.isStyleLoaded()) {
      map.once("load", () => map.fitBounds(bounds, { padding }));
    } else {
      map.fitBounds(bounds, { padding });
    }
  }, [mapRef, points, padding]);
}